import React, { useState } from 'react';
import { formatOrderRef } from '../../utils/orderRef';
import { ChevronDown, ChevronRight, FileText, Download, Eye, User } from 'lucide-react';
import StatusBadge from '../common/StatusBadge';
import NotificationBadge from '../common/NotificationBadge';
import { PROSTHESIS_TYPE_LABELS, ORDER_STATUS, getSubtypeLabel } from '../../utils/constants';
import { filesApi } from '../../services/api';
import { formatPatientDisplay } from '../../utils/nameNormalizer';

const OrderTableRow = ({ order, onOrderClick, onPreviewFile, onPatientClick, unreadCount = 0, index = 0 }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    // Calculate stagger delay class (max 10 rows visible at a time)
    const staggerClass = index < 10 ? `stagger-${index + 1}` : '';

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const handleDownload = async (file) => {
        if (!file.id) {
            console.log(`Impossible de télécharger le fichier: ${file.name} - ID manquant`);
            return;
        }

        try {
            console.log('Téléchargement du fichier avec authentification:', file.id);
            
            // Utiliser l'API authentifiée pour télécharger le fichier
            const blob = await filesApi.downloadBlob(file.id);
            
            // Créer une URL locale à partir du blob et déclencher le téléchargement
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = file.name || 'fichier';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            // Nettoyer l'URL blob après utilisation
            URL.revokeObjectURL(blobUrl);
            
            console.log('Fichier téléchargé avec succès');
        } catch (error) {
            console.error('Erreur lors du téléchargement du fichier:', error);
        }
    };

    const isPreviewable = (fileName) => {
        const extension = fileName.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'stl'].includes(extension);
    };

    return (
        <>
            {/* Ligne principale du tableau */}
            <tr className={`hover:bg-white/10 border-b border-white/10 cursor-pointer transition-colors row-hover-lift animate-fade-in ${staggerClass} ${
                order.status === ORDER_STATUS.CANCELLED ? 'glass opacity-60' : ''
            }`}
                style={{ opacity: 0 }}
                onClick={() => onOrderClick && onOrderClick(order)}>
                {/* Bouton d'expansion */}
                <td className="px-6 py-4 w-12">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                        className="text-gray-500 hover:text-gray-300 transition-colors"
                    >
                        {isExpanded ? (
                            <ChevronDown className="h-5 w-5" />
                        ) : (
                            <ChevronRight className="h-5 w-5" />
                        )}
                    </button>
                </td>

                {/* Numéro de commande */}
                <td className="px-6 py-4 font-medium text-white w-20">
                    <div className="flex items-center gap-2">
                        <NotificationBadge count={unreadCount} size="small" />
                        {formatOrderRef(order)}
                    </div>
                </td>

                {/* Patient */}
                <td className="px-6 py-4">
                    {order.patient_name_code ? (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onPatientClick && onPatientClick({
                                    patientNameCode: order.patient_name_code
                                });
                            }}
                            className="text-sm font-medium text-yellow-400 hover:text-yellow-300 hover:underline cursor-pointer transition-colors flex items-center"
                            title="Voir l'historique des commandes pour ce patient"
                        >
                            <User className="h-3 w-3 mr-1" />
                            {order.patient_name_code}
                        </button>
                    ) : (
                        <span className="text-sm text-gray-500 italic">Non renseigné</span>
                    )}
                </td>

                {/* Type de prothese */}
                <td className="px-6 py-4 text-sm text-gray-300 w-32">
                    {order.works && order.works.length > 0 ? (
                        <div>
                            <div className="font-medium">{PROSTHESIS_TYPE_LABELS[order.works[0].prosthesisType]}</div>
                            {order.works[0].prosthesisSubtype && (
                                <div className="text-xs text-gray-400">{getSubtypeLabel(order.works[0].prosthesisType, order.works[0].prosthesisSubtype)}</div>
                            )}
                            {order.works.length > 1 && (
                                <div className="text-xs text-yellow-400 mt-1">+{order.works.length - 1} autre{order.works.length > 2 ? 's' : ''}</div>
                            )}
                            {order.works.flatMap(w => w.toothNumbers || []).length > 0 && (
                                <div className="text-xs text-gray-400 mt-1">🦷 {order.works.flatMap(w => w.toothNumbers || []).join(', ')}</div>
                            )}
                        </div>
                    ) : (
                        <span className="text-gray-500 italic">-</span>
                    )}
                </td>

                {/* Description courte */}
                <td className="px-6 py-4 text-sm text-gray-400 min-w-64">
                    <div className="max-w-md">
                        {order.works && order.works.length > 0 ? (
                            order.works[0].description || <span className="italic">Pas de description</span>
                        ) : (
                            <span className="italic">-</span>
                        )}
                    </div>
                </td>

                {/* Date de commande */}
                <td className="px-6 py-4 text-sm text-gray-400 w-32">
                    {formatDate(order.orderDate)}
                </td>

                {/* Date de livraison */}
                <td className="px-6 py-4 text-sm w-32 text-gray-400">
                    {formatDate(order.deliveryDate)}
                </td>

                {/* Statut */}
                <td className="px-6 py-4 w-32">
                    <StatusBadge status={order.status} />
                </td>

                {/* Actions */}
                <td className="px-6 py-4 w-32">
                    <button
                        onClick={() => onOrderClick(order)}
                        className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded text-sm font-medium transition-colors flex items-center space-x-2"
                    >
                        <Eye className="h-4 w-4" />
                        <span>Voir</span>
                    </button>
                </td>
            </tr>

            {/* Ligne expandée avec détails */}
            {isExpanded && (
                <tr className="bg-white/10 backdrop-blur-xl">
                    <td colSpan="9" className="px-6 py-6">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Informations détaillées */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-white text-sm">Informations de la commande</h4>
                                <div className="bg-black/30 p-3 rounded-md border border-white/10 space-y-2">
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-400">Cabinet:</span>
                                        <span className="text-gray-200">{order.clientInfo.clinic}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-400">Telephone:</span>
                                        <span className="text-gray-200">{order.clientInfo.phone}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-400">Email:</span>
                                        <span className="text-gray-200">{order.clientInfo.email}</span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                        <span className="text-gray-400">Adresse:</span>
                                        <span className="text-gray-200">{order.clientInfo.address}</span>
                                    </div>
                                </div>

                                {/* Details travaux */}
                                <h4 className="font-medium text-white text-sm">Travaux ({order.works?.length || 0})</h4>
                                {order.works && order.works.length > 0 ? (
                                    <div className="space-y-2">
                                        {order.works.map((work, index) => (
                                            <div key={index} className="bg-black/30 rounded-md border border-white/10 overflow-hidden">
                                                <div className="bg-indigo-900/30 px-3 py-1.5 border-b border-white/10 flex items-center justify-between">
                                                    <span className="text-xs text-indigo-300 font-medium">Travail {index + 1}</span>
                                                    <span className="text-xs text-gray-200 font-medium">
                                                        {PROSTHESIS_TYPE_LABELS[work.prosthesisType]}
                                                        {work.prosthesisSubtype && ` - ${getSubtypeLabel(work.prosthesisType, work.prosthesisSubtype)}`}
                                                    </span>
                                                </div>
                                                <div className="p-3">
                                                    <div className="flex flex-wrap gap-2 mb-2">
                                                        <span className="inline-flex items-center gap-1 bg-white/10 px-2 py-0.5 rounded text-xs text-gray-200">
                                                            🦷 {work.toothNumbers?.join(', ') || '-'}
                                                        </span>
                                                        {work.shade && (
                                                            <span className="inline-flex items-center gap-1 bg-blue-900/40 px-2 py-0.5 rounded text-xs text-blue-200">
                                                                🎨 {work.shade}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {work.description && (
                                                        <p className="text-xs text-gray-400 leading-relaxed">{work.description}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-black/30 p-3 rounded-md border border-white/10 text-center text-sm text-gray-500">
                                        Aucun travail défini
                                    </div>
                                )}
                            </div>

                            {/* Fichiers joints */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-white text-sm">Fichiers joints</h4>
                                {order.files && order.files.length > 0 ? (
                                    <div className="bg-black/30 p-3 rounded-md border border-white/10 space-y-2">
                                        {order.files.map((file, index) => (
                                            <div key={index} className="flex items-center justify-between glass p-2 rounded">
                                                <div className="flex items-center">
                                                    <FileText className="h-4 w-4 text-gray-500 mr-2" />
                                                    <span className="text-sm text-gray-300">{file.name}</span>
                                                    <span className="text-xs text-gray-500 ml-2">({file.size})</span>
                                                </div>
                                                <div className="flex items-center space-x-1">
                                                    {isPreviewable(file.name) && (
                                                        <button
                                                            onClick={() => onPreviewFile(file)}
                                                            className="text-yellow-400 hover:text-yellow-300 p-1 rounded hover:bg-yellow-900/30 transition-colors"
                                                            title="Prévisualiser"
                                                        >
                                                            <Eye className="h-4 w-4" />
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={() => handleDownload(file)}
                                                        className="text-green-400 hover:text-green-300 p-1 rounded hover:bg-green-900/30 transition-colors"
                                                        title="Télécharger"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-black/30 p-3 rounded-md border border-white/10 text-center text-sm text-gray-500">
                                        Aucun fichier joint
                                    </div>
                                )}

                            </div>
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
};

export default OrderTableRow;